import React from 'react';
import styled from 'styled-components';

/**
 * "More like this" sticker — a small secondary affordance overlaid on the
 * bottom-LEFT of a feed card's photo. Tapping it opens that product's PDP and
 * lands on the PDP's "More like this" rail, instead of the top of the sheet
 * (which is what a photo/title tap does).
 *
 * Placement notes, all deliberate:
 *   • It is rendered as a positioned SIBLING inside `ResultsProductCard`'s
 *     `ImageFrame` (via the `imageOverlay` slot), after the transparent
 *     `OpenButton`. Not inside it — that would nest a button in a button; not
 *     below the card — that would be untappable over the photo; and NOT in a
 *     new wrapper around `ImageFrame`, because `BoardFeed`'s 6:5 photo override
 *     is a structural `> div:first-child > div > div:first-child` selector.
 *   • `z-index: 11` clears the card's `OverflowWrap` (z-index 10), which spans
 *     the full card width at `bottom: 10px` with `pointer-events: auto` on its
 *     inner block — without this the overflow row would swallow taps landing on
 *     the sticker's right half.
 *   • Bottom-left with an 8px inset puts it just above the connected Save
 *     drawer (which sits BELOW the photo), so the dominant Save CTA is never
 *     crowded — the drawer is outside the photo frame, so growing the pill
 *     cannot encroach on it. Opaque white — not translucent — so the clay label
 *     stays legible over real product photography.
 *
 * The click handler stops propagation so the tap never reaches the card's
 * open path or the Save bar.
 */
/* Sized at 1.2x the original 28px-tall pill (THE-456 follow-up, revised): users
   were not noticing it on the page. Two separate rules, deliberately:
     • The PILL BOX grows 20% — height 28->34, padding 10->12, gap 5->6.
     • The TYPE does not follow that scale. Font size and icon size are pinned
       to the Save drawer's (14px / 14px, weight 600, gap 6px) so the sticker
       reads as a sibling of Save rather than its own type scale. Note Save is
       14px and this pill was 12px, so this is a 2px increase, not a no-op.
   Unchanged on purpose AT THE TIME: the 8px inset (bottom-left placement is
   specified — this is a size change, not a relocation) and the original
   shadow. (THE-1185, QA round 3, 2026-10-26: the 1px border mentioned here
   is gone — the pill spec below drops it for an on-photo shadow-only
   treatment, so this line no longer describes the current rule; see that
   comment, not this one, for the border.) */
/* THE-1185 — the one pill spec, Update picks' match: 40px tall, 16px side
   padding, 8px gap, radius.pill, weight.semibold, ink text — and "on photos
   the border becomes a soft shadow" (this sticker sits on a product photo,
   so it drops the border other pills carry and keeps only the shadow for
   edge definition). Font-size stays a literal 14px on purpose: it is pinned
   to CardSaveDrawer's matching 14px/600 Save-bar type (see the file doc
   comment above — "reads as a sibling of Save, not its own type scale"),
   which is outside this ticket's named scope and outside theme.text's 4
   roles; moving it alone would un-pin that pairing. 14px also happens to be
   exactly theme.text.small's new desktop value, so this pinning and the
   board's one type scale no longer disagree. */
const Sticker = styled.button<{ $position: 'bottom-left' | 'top-left' }>`
  position: absolute;
  left: 8px;
  ${({ $position }) => ($position === 'top-left' ? 'top: 8px;' : 'bottom: 8px;')}
  z-index: 11;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.pill.gap};
  height: ${({ theme }) => theme.pill.height};
  padding: 0 ${({ theme }) => theme.pill.paddingX};
  border-radius: ${({ theme }) => theme.pill.radius};
  /* Opaque, never translucent: the pill sits on unpredictable product photos
     and a translucent fill is unreadable the moment the image goes light.
     This is a button element, so an explicit border: none is required —
     without it the browser's default button border (2px outset) shows
     through, which is exactly the bug this comment exists to prevent. */
  border: none;
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  color: ${({ theme }) => theme.color.foreground};
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 150ms ease;

  &:hover {
    background: #faf8f6;
  }
  &:active {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: 2px solid hsl(var(--ring));
    outline-offset: 2px;
  }
`;

// Stacked cards — two offset rounded rectangles, reading as "more of these".
const StackedCardsGlyph: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="9" y="3" width="12" height="12" rx="2.5" />
    <path d="M15 19.5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 3 19.5v-7A2.5 2.5 0 0 1 5.5 10" />
  </svg>
);

export interface MoreLikeThisStickerProps {
  /** Product title — used for the distinct accessible name. */
  title: string;
  onClick: () => void;
  /**
   * Which corner of the photo the pill sits in. Defaults to `bottom-left`
   * (the board feed, per the placement notes above). The gift-guide hub's
   * full-width single-column card (THE-825, canvas p05) puts the connected
   * Save bar directly under the photo with nothing to crowd, and the design
   * calls for the pill at the TOP of the image instead.
   */
  position?: 'bottom-left' | 'top-left';
}

export const MoreLikeThisSticker: React.FC<MoreLikeThisStickerProps> = ({
  title,
  onClick,
  position = 'bottom-left',
}) => (
  <Sticker
    type="button"
    $position={position}
    data-more-like-this
    // Distinct from the card's "Open {title}" and the drawer's "Save" — a
    // screen-reader user hears three clearly different actions on one card.
    aria-label={`More like this: ${title}`}
    onClick={(e) => {
      // The card's open path (the OpenButton beneath) and the Save drawer both
      // live inside the same click target chain; this tap is neither.
      e.stopPropagation();
      onClick();
    }}
    // Keep a pointerdown from starting the card's drag/press affordances.
    onPointerDown={(e) => e.stopPropagation()}
  >
    <StackedCardsGlyph />
    More like this
  </Sticker>
);

export default MoreLikeThisSticker;
